import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css'; // Include your CSS styles

import agentAvatar from './assets/bot.png'
import userAvator from './assets/user.png'
import { API_BASE_URL } from './config/api';

function App({ sessionId }) {
  const [inputValue, setInputValue] = useState('');
  const [chatDisabled, setChatDisabled] = useState(false);
  const [messages, setMessages] = useState([]);
  const endOfMessagesRef = useRef(null);
  const [conclude, setConclude] = useState(false);
  const inputTimeoutRef = useRef(null);
  const navigate = useNavigate();


  const startTypingIndicator = () => {
    let dotCount = 1;
    const intervalId = setInterval(() => {
      dotCount = (dotCount % 3) + 1;
      const dots = '.'.repeat(dotCount);

      setMessages(prevMessages => {
        const newMessages = [...prevMessages];
        newMessages[newMessages.length - 1].text = dots;
        return newMessages;
      });
    }, 500);

    return intervalId;
  };

  useEffect(() => {
    const fetchInitialMessage = async () => {
      const botTypingMessage = {
        text: '.',
        avatar: agentAvatar,
        isUser: false
      };
      setMessages([botTypingMessage]);

      const intervalId = startTypingIndicator();

      try {
        const response = await fetch(`${API_BASE_URL}/chat/${sessionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({})
        });
        if (response.status === 410) {// chat completed
          navigate(`/${sessionId}/completed`);
        }
        const data = await response.json();
        clearInterval(intervalId);

        const initialBotMessage = {
          text: data.message,
          avatar: agentAvatar,
          isUser: false
        };
        setMessages([initialBotMessage]);

        if (response.status !== 200) {
          setChatDisabled(true);
        }

      } catch (error) {
        clearInterval(intervalId);
        console.error('Error fetching initial message:', error);
        const errorBotMessage = {
          text: "Sorry I'm having trouble chatting to you. Please try again later.",
          avatar: agentAvatar,
          isUser: false
        };
        setMessages([errorBotMessage]);
        setChatDisabled(true);
      }
    };

    if(!sessionId){
      const botMessage = {
        text: 'Sorry, I can only talk to you if my agent asks me to.',
        avatar: agentAvatar,
        isUser: false
      };
      setMessages([botMessage]);
      setChatDisabled(true);
    }else{
      fetchInitialMessage();
    }
    
    return () => {
      if (conclude) {
        closeChat();
      }
    };
  }, [sessionId]);



  useEffect(() => {
    // Scroll to the bottom whenever messages change
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (conclude) {
      inputTimeoutRef.current = setTimeout(() => {
        closeChat();
      }, 10000);

      return () => {
        if (inputTimeoutRef.current) {
          clearTimeout(inputTimeoutRef.current);
        }
      };
    }
  }, [conclude, inputValue]);

  const closeChat = async () => {
    await fetch(`${API_BASE_URL}/complete/${sessionId}`, {
      method: 'POST',
    });
    navigate(`/${sessionId}/completed`);;
  }

  const sendMessage = async () => {
    const userMessage = inputValue.trim();
    if (!userMessage) return;

    const userMessageObj = {
      text: userMessage,
      avatar: userAvator, // Placeholder for user's avatar
      isUser: true
    };

    setMessages(prevMessages => [...prevMessages, userMessageObj]);
    setInputValue('');

    const botTypingMessage = {
      text: '.',
      avatar: agentAvatar,
      isUser: false
    };
    setMessages(prevMessages => [...prevMessages, botTypingMessage]);

    const intervalId = startTypingIndicator();

    try {
      const response = await fetch(`${API_BASE_URL}/chat/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: userMessage })
      });
      const data = await response.json();
      clearInterval(intervalId);

      const botMessage = {
        text: data.message,
        avatar: agentAvatar,
        isUser: false
      };
      setConclude(data.conclude);
      setMessages(prevMessages => {
        const newMessages = [...prevMessages];
        newMessages[newMessages.length - 1] = botMessage;
        if (data.conclude) {
          newMessages.push({
            text: "Chat will close in 10 seconds, or ",
            link: { text: "click here to close the chat", onClick: closeChat },
            avatar: agentAvatar,
            isUser: false
          });
        }
        return newMessages;
      });

      if (conclude) {
        clearTimeout(inputTimeoutRef.current);
        inputTimeoutRef.current = setTimeout(() => {
          closeChat();
        }, 10000);
      }

      if (response.status !== 200) {
        setChatDisabled(true);
      }

    } catch (error) {
      clearInterval(intervalId);
      console.error('Error fetching initial message:', error);
      setChatDisabled(true);
    }
  };

  return (
    <div className="App chat-container">
      <div className="chat-box">
        <div className="disclaimer-box">
          <p>
            Please note, this conversation is driven by AI, the agent will review the message and follow up with you personally if needed. Thanks for your understanding.
          </p>
          <button className="dismiss-button" onClick={() => document.querySelector('.disclaimer-box').style.display = 'none'}>
            &times;
          </button>
        </div>
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.isUser ? 'user-message' : 'bot-message'}`}>
            <img className={`message ${msg.isUser ? 'user-avatar' : 'bot-avatar'}`} src={msg.avatar} />
            <span className={msg.isUser ? 'user-text' : 'bot-text'}>{msg.text}
              {msg.link && <a className="close-chat-button" onClick={msg.link?.onClick}>{msg.link?.text}</a>}
            </span>
          </div>
        ))}
        <div ref={endOfMessagesRef} />
      </div>
      <div className="input-area">
        <textarea
          className="input-box"
          placeholder="Type a message..."
          disabled={chatDisabled}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
        />
        <button className="send-button" onClick={sendMessage} disabled={chatDisabled}>
          Send
        </button>
      </div>
    </div>
  );
}

export default App;